import {extractFilenameFromPath, getMediaUrl} from '../../../utils/api';
import {getApiBaseUrl} from '../../../config/app-config';
import { setupDocumentDownloadHandlers } from './element-setup';

export function applyContentToElement(element, data) {
    if (!data) return;

    // Special handling for YouTube videos
    if (element.getAttribute('data-content') === 'youtube-video') {
        // Explicitly set attribute
        element.setAttribute('data-attribute', 'video');
        element.setAttribute('data-original-attribute', 'video');

        // Check for video URL in content or media_path
        const youtubeUrl = data.content || data.media_path;

        if (youtubeUrl) {
            // Save URL in attribute for video-player handler
            element.setAttribute('data-media-path', youtubeUrl);

            // Ensure element has play button
            if (!element.querySelector('.play-button')) {
                const thumbnail = document.createElement('img');
                thumbnail.src = 'images/video/image.png'; // Use default image
                thumbnail.alt = 'Video thumbnail';
                thumbnail.className = 'video-thumbnail';

                const playButton = document.createElement('button');
                playButton.className = 'play-button';

                element.appendChild(thumbnail);
                element.appendChild(playButton);
            }
        }
    }
    // Handle document elements
    else if (element.getAttribute('data-type') === 'document' ||
        element.getAttribute('data-attribute') === 'document') {

      // Set attributes
      element.setAttribute('data-attribute', 'document');
      element.setAttribute('data-type', 'document');

      // Check for file path
      const documentPath = data.media_path;

      if (documentPath) {
        // Save path in attribute
        element.setAttribute('data-media-path', documentPath);

        // If this is an anchor element (e.g. certificate download link), set href directly
        if (element.tagName === 'A' || element.classList.contains('news-link')) {
          const filename = extractFilenameFromPath(documentPath);
          if (filename) {
            const downloadUrl = `${getApiBaseUrl()}/api/file_document/${filename}`;
            element.setAttribute('href', downloadUrl);
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noopener noreferrer');
          }
        }
        // If this is cooperation-docs element
        else if (element.classList.contains('cooperation-docs')) {
          // Check for dropdown icon
          let dropdownIcon = element.querySelector('img[src*="Dropdown"]');

          if (dropdownIcon) {
            // Add tooltip and cursor style
            dropdownIcon.title = `Download document: ${extractFilenameFromPath(documentPath)}`;
            dropdownIcon.style.cursor = 'pointer';

            // Add attribute for file access
            dropdownIcon.setAttribute('data-document-path', documentPath);
            setTimeout(() => {
              setupDocumentDownloadHandlers();
            }, 100);
          }
        }
        // If element contains an anchor with news-link class, update it
        else {
          const anchor = element.querySelector('a.news-link');
          if (anchor) {
            const filename = extractFilenameFromPath(documentPath);
            if (filename) {
              const downloadUrl = `${getApiBaseUrl()}/api/file_document/${filename}`;
              anchor.setAttribute('href', downloadUrl);
              anchor.setAttribute('target', '_blank');
              anchor.setAttribute('rel', 'noopener noreferrer');
            }
          }
        }
      }
    }
    else {
        const contentType = element.getAttribute('data-type') || 'text';

        if (contentType === 'image' && data.media_path) {
            applyImageContent(element, data);
        } else if (data.content) {
            applyTextContent(element, data);
        }
    }

    saveDataAttributes(element, data);
}

// Function to extract YouTube ID from URL
function extractYoutubeId(url) {
    if (!url) return null;

    // If it's just a video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
    }

    // YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    return (match && match[2].length === 11) ? match[2] : null;
}

// Function to update YouTube player
function updateYoutubePlayer(element, videoId) {
    // Create YouTube iframe
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${videoId}?rel=0`;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.style.borderRadius = 'inherit';

    // Clear element content and add iframe
    element.innerHTML = '';
    element.appendChild(iframe);
}

function applyImageContent(element, data) {
    const filename = extractFilenameFromPath(data.media_path);
    const imageUrl = getMediaUrl(filename);

    if (element.tagName.toLowerCase() === 'img') {
        updateImageElement(element, imageUrl, data);
    } else {
        replaceWithImageElement(element, imageUrl, data);
    }
}

function updateImageElement(imgElement, imageUrl, data) {
    imgElement.src = imageUrl;
    imgElement.alt = data.tag || data.tagId || '';
    imgElement.setAttribute('data-original-src', imageUrl);
}

function replaceWithImageElement(element, imageUrl, data) {
    const imgElement = document.createElement('img');
    imgElement.src = imageUrl;
    imgElement.alt = data.tag || data.tagId || '';
    imgElement.className = element.className;
    imgElement.setAttribute('data-original-src', imageUrl);

    Array.from(element.attributes).forEach(attr => {
        if (attr.name.startsWith('data-')) {
            imgElement.setAttribute(attr.name, attr.value);
        }
    });

    if (element.parentNode) {
        element.parentNode.replaceChild(imgElement, element);
    }
}

function applyTextContent(element, data) {
    const isHtml = element.getAttribute('data-attribute') === 'html' || data.data_attribute === 'html';
    if (isHtml) {
        element.innerHTML = data.content;
    } else {
        element.textContent = data.content;
    }
    element.setAttribute('data-original-content', data.content);
}

function saveDataAttributes(element, data) {
    if (data.data_attribute) {
        element.setAttribute('data-attribute', data.data_attribute);
    }

    if (data.block_id) {
        element.setAttribute('data-block-id', data.block_id);
    }
}